import { Component, ElementRef, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AlgorithmData } from '../../../state/algorithm.state';
import { AlgorithmUsage } from '@protocols/use-cases';
import { AlgorithmUsageTableComponent, PipelineUsageEntry } from '../widgets/usage-table/usage-table.component';
import { AlgorithmUsageData } from '../page/algorithms.facade';

export enum AlgorithmColumn {
  NAME = 'name',
  VERSION = 'version',
  ORGANIZATIONS = 'organizations',
  WORKSPACES = 'workspaces',
  REGULATED = 'regulated',
  ENVIRONMENT = 'environment',
  STATUS = 'status',
  EDIT = 'edit',
}

export interface PipelineStepAction {
  algorithmId: string;
  pipelineStepId: string;
}

@Component({
  selector: 'ts-algorithm-list',
  templateUrl: './algorithm-list.component.html',
  styleUrls: ['./algorithm-list.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AlgorithmUsageTableComponent,
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AlgorithmListComponent implements OnChanges {
  @Input() items: AlgorithmData[] = [];
  @Input() usages: Record<string, AlgorithmUsageData> = {};
  @Input() loading = false;
  @Input() noAlgorithms = false;
  @Input() language = 'en';
  @Input() expandable = true;
  @Input() columns: AlgorithmColumn[] = [
    AlgorithmColumn.NAME,
    AlgorithmColumn.VERSION,
    AlgorithmColumn.ORGANIZATIONS,
    AlgorithmColumn.WORKSPACES,
    AlgorithmColumn.REGULATED,
    AlgorithmColumn.ENVIRONMENT,
    AlgorithmColumn.STATUS,
  ];

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  @Output() deprecateClicked = new EventEmitter<AlgorithmData>();
  @Output() navigateToPipeline = new EventEmitter<string>();
  @Output() usageRequested = new EventEmitter<string>();

  @Output() editPipelineStep = new EventEmitter<PipelineStepAction>();
  @Output() deleteFromPipelineStep = new EventEmitter<PipelineStepAction>();
  @Output() editClicked = new EventEmitter<AlgorithmData>();

  expandedAlgorithm: AlgorithmData | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  get visibleColumns(): string[] {
    return this.columns.map((col) => col.valueOf());
  }

  onEditPipelineStep(algorithmId: string, pipeline: PipelineUsageEntry): void {
    this.editPipelineStep.emit({ algorithmId, pipelineStepId: pipeline.stepId });
  }

  onDeleteFromPipelineStep(algorithmId: string, pipeline: PipelineUsageEntry): void {
    this.deleteFromPipelineStep.emit({ algorithmId, pipelineStepId: pipeline.stepId });
  }

  toggleRow(algorithm: AlgorithmData): void {
    if (!this.expandable) {
      return;
    }

    this.expandedAlgorithm = this.expandedAlgorithm?.id === algorithm.id ? null : algorithm;

    // Request usage for the algorithm if not already available
    if (!(algorithm.id in this.usages)) {
      this.usageRequested.emit(algorithm.id);
    }
  }

  /**
   * Returns true if the algorithm row is expanded.
   */
  isExpanded(algorithm: AlgorithmData): boolean {
    return this.expandedAlgorithm?.id === algorithm.id;
  }

  /**
   * Returns true if usage data is currently loading for the algorithm.
   */
  isUsageLoading(algorithmId: string): boolean {
    return !(algorithmId in this.usages) || this.usages[algorithmId] === undefined || this.usages[algorithmId].loading;
  }

  /**
   * Returns true if there was an error loading usage data.
   */
  isUsageError(algorithmId: string): boolean {
    return this.usages[algorithmId]?.error !== undefined;
  }

  /**
   * Returns the usage data for the algorithm, or null if not available or error.
   */
  getUsage(algorithmId: string): AlgorithmUsage | null {
    const usage = this.usages[algorithmId];
    if (usage && usage.data !== undefined && usage.error === undefined) {
      return usage.data;
    }
    return null;
  }

  /**
   * Returns the error message for the algorithm, or null if no error.
   */
  getErrorMessage(algorithmId: string): string | null {
    const usage = this.usages[algorithmId];
    if (usage?.error) {
      return usage.error;
    }
    return null;
  }

  /**
   * Returns true if the usage data has no pipelines.
   */
  hasNoPipelines(algorithmId: string): boolean {
    const usage = this.getUsage(algorithmId);
    return usage?.pipelines ? Object.keys(usage.pipelines).length === 0 : false;
  }
}
